import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { userAuthApi } from '../services/api';

const billingTypeLabel = { ONE_TIME: 'One-time', WEEKLY: 'Weekly', MONTHLY: 'Monthly' };

const statusStyles = {
  COMPLETED: 'bg-green-500/15 text-green-300 border-green-500/30',
  CREATED:   'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  APPROVED:  'bg-blue-500/15 text-blue-300 border-blue-500/30',
  VOIDED:    'bg-red-500/15 text-red-300 border-red-500/30',
};

const statusLabels = {
  PENDING: 'Pending',
  COMPLETED: 'Completed',
  CREATED: 'Processing',
  APPROVED: 'Approved',
  VOIDED: 'Voided',
  CANCELLED: 'Cancelled',
};

const statusLabel = (status) => statusLabels[status] || status;

const billingLabel = {
  ONE_TIME: 'One-time',
  DAILY:    'Daily',
  WEEKLY:   'Weekly',
  MONTHLY:  'Monthly',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const UserProfilePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await userAuthApi.getCurrentUser();
        if (response.data?.authenticated) {
          setUser(response.data.user);
        } else {
          navigate('/login');
        }
      } catch (error) {
        // Network/CORS error — fall back to localStorage so the user isn't
        // kicked to login just because the API is temporarily unreachable.
        if (!error.response) {
          try {
            const cached = JSON.parse(localStorage.getItem('currentUser') || 'null');
            if (cached?.email) { setUser(cached); return; }
          } catch (e) {}
        }
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    const fetchPayments = async () => {
      try {
        const response = await userAuthApi.getMyPayments();
        if (response.data?.success) {
          setPayments(response.data.payments || []);
        }
      } catch (error) {
      } finally {
        setPaymentsLoading(false);
      }
    };

    const fetchInvoices = async () => {
      try {
        const response = await userAuthApi.getMyInvoices();
        if (response.data?.success) {
          setPendingInvoices(response.data.invoices || []);
        }
      } catch (error) {
      } finally {
        setInvoicesLoading(false);
      }
    };

    fetchProfile();
    fetchPayments();
    fetchInvoices();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#04050c] text-white">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#04050c] text-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-16 space-y-8">

        {/* Profile Card */}
        <div className="rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-[#0a0f21] to-[#060816] p-8 shadow-[0_0_40px_rgba(34,211,238,0.15)] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-pink-500/10 blur-[100px] rounded-full pointer-events-none"></div>

          <h1 className="text-3xl font-bold text-white mb-8 border-b border-white/10 pb-4 relative z-10">My Profile</h1>

          {user && (
            <div className="space-y-6 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm text-slate-400 mb-1">First Name</p>
                  <p className="text-xl font-semibold text-white">{user.firstName || 'Not provided'}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm text-slate-400 mb-1">Last Name</p>
                  <p className="text-xl font-semibold text-white">{user.lastName || 'Not provided'}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm text-slate-400 mb-1">Email Address</p>
                  <p className="text-xl font-semibold text-cyan-300">{user.email || 'Not provided'}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm text-slate-400 mb-1">Phone Number</p>
                  <p className="text-xl font-semibold text-white">{user.phone || 'Not provided'}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 md:col-span-2 p-6">
                  <p className="text-sm text-slate-400 mb-1">Company Name</p>
                  <p className="text-xl font-semibold text-fuchsia-300">{user.companyName || 'Not provided'}</p>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-slate-400 mb-2">Welcome back to your JOFFON dashboard.</p>
                </div>
                <Link
                  to="/secure-payment"
                  className="flex-shrink-0 inline-flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-400/10 px-6 py-3 text-sm font-semibold uppercase tracking-widest text-cyan-200 transition-all duration-300 hover:border-cyan-300 hover:bg-cyan-400/20 hover:text-white hover:shadow-[0_0_18px_rgba(34,211,238,0.35)]"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Proceed to Payment
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Pending Invoices */}
        {(invoicesLoading || pendingInvoices.length > 0) && (
          <div className="rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-[#120f04] to-[#060816] p-8 shadow-[0_0_40px_rgba(234,179,8,0.08)]">
            <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-500/20 border border-yellow-500/40">
                <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Pending Invoices</h2>
                <p className="text-sm text-slate-400 mt-1">Invoices awaiting payment</p>
              </div>
            </div>

            {invoicesLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
              </div>
            ) : (
              <div className="space-y-4">
                {pendingInvoices.map((inv) => (
                  <div key={inv._id} className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-5 hover:border-yellow-400/40 transition-all">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="rounded-full border border-yellow-500/40 bg-yellow-500/10 px-3 py-0.5 text-xs font-semibold text-yellow-300 tracking-wide">
                            {statusLabel('PENDING')}
                          </span>
                          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-0.5 text-xs text-slate-400">
                            {billingTypeLabel[inv.billingType] || inv.billingType}
                          </span>
                        </div>
                        <p className="text-white font-semibold text-base">{inv.productName}</p>
                        {inv.productCategory && (
                          <p className="text-xs text-yellow-300/60 uppercase tracking-widest mt-0.5">{inv.productCategory}</p>
                        )}
                        {inv.description && (
                          <p className="text-sm text-slate-400 mt-2">{inv.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
                          <span>Invoice: <span className="font-mono text-slate-400">{inv.invoiceNumber}</span></span>
                          {inv.dueDate && (
                            <span>Due: <span className="text-slate-400">{new Date(inv.dueDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span></span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 flex flex-col items-end gap-3">
                        <div className="text-right">
                          {inv.feeAmount > 0 ? (
                            <>
                              <p className="text-xs text-slate-500">Amount: ${Number(inv.amount).toFixed(2)}</p>
                              <p className="text-xs text-slate-500">Fee ({inv.feePercent}%): ${Number(inv.feeAmount).toFixed(2)}</p>
                              <p className="text-2xl font-bold text-yellow-400">${Number(inv.totalAmount).toFixed(2)}</p>
                            </>
                          ) : (
                            <p className="text-2xl font-bold text-yellow-400">${Number(inv.totalAmount ?? inv.amount).toFixed(2)}</p>
                          )}
                        </div>
                        <Link
                          to={`/secure-payment?invoiceNumber=${encodeURIComponent(inv.invoiceNumber)}`}
                          className="inline-flex items-center gap-2 rounded-xl border border-yellow-400/40 bg-yellow-400/10 px-5 py-2.5 text-sm font-semibold text-yellow-200 hover:border-yellow-300 hover:bg-yellow-400/20 transition-all"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          Pay Now
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payment History */}
        <div className="rounded-2xl border border-fuchsia-500/30 bg-gradient-to-br from-[#0c0817] to-[#060816] p-8 shadow-[0_0_40px_rgba(217,70,239,0.1)]">
          <div className="flex items-center gap-4 mb-8 border-b border-white/10 pb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-fuchsia-500/20 border border-fuchsia-500/40 shadow-[0_0_15px_rgba(217,70,239,0.2)]">
              <svg className="w-6 h-6 text-fuchsia-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Payment History</h2>
              <p className="text-sm text-slate-400 mt-1">All payments made under your account</p>
            </div>
          </div>

          {paymentsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fuchsia-500"></div>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/5 border border-white/10">
                  <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <p className="text-slate-400 font-medium">No payments yet</p>
              <p className="text-slate-500 text-sm mt-1">Your completed payments will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {payments.map((p) => (
                <div
                  key={p._id}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:border-fuchsia-500/30 hover:bg-fuchsia-500/5 transition-all duration-200"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold tracking-wide ${statusStyles[p.status] || 'bg-slate-500/15 text-slate-300 border-slate-500/30'}`}>
                          {statusLabel(p.status)}
                        </span>
                        {p.billingPreference && (
                          <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-0.5 text-xs text-slate-400">
                            {billingLabel[p.billingPreference] || p.billingPreference}
                          </span>
                        )}
                      </div>
                      <p className="text-white font-semibold text-base leading-snug">{p.productName || '—'}</p>
                      {p.productCategory && (
                        <p className="text-xs text-fuchsia-300/70 uppercase tracking-widest mt-0.5">{p.productCategory}</p>
                      )}
                      {p.description && (
                        <p className="text-sm text-slate-400 mt-2 leading-relaxed">{p.description}</p>
                      )}
                      <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
                        {p.invoiceNumber && <span>Invoice: <span className="font-mono text-slate-400">{p.invoiceNumber}</span></span>}
                        <span>Date: <span className="text-slate-400">{formatDate(p.capturedAt || p.createdAt)}</span></span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-2xl font-bold text-cyan-400">${Number(p.amount).toFixed(2)}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{p.currency || 'USD'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </main>
    </div>
  );
};

export default UserProfilePage;
